import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n/LanguageContext";

const maskReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.15 },
  }),
};

export default function Hero() {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const orbY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen flex items-center overflow-hidden grid-pattern" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 opacity-50">
        <img
          src="/hero-bg.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#15171C]/60 via-[#15171C]/70 to-[#15171C]" />
      </motion.div>

      <motion.div
        style={{ y: orbY }}
        className="absolute -end-40 top-1/4 w-[520px] h-[520px] rounded-full bg-[#5B7FFF]/10 blur-[140px] pointer-events-none"
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-32 pb-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="inline-flex items-center gap-2 glow-badge rounded-full px-4 py-1.5 text-xs font-medium tracking-widest uppercase mb-8"
          data-testid="hero-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#5B7FFF] animate-pulse" />
          {t.hero.badge}
        </motion.div>

        <h1 key={lang} className="font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] max-w-5xl" data-testid="hero-headline">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={i}
                variants={maskReveal}
                initial="hidden"
                animate="visible"
              >
                {line.pre}
                {line.accent && <span className="text-[#5B7FFF]">{line.accent}</span>}
                {line.post}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-8 text-base md:text-lg text-slate-400 leading-relaxed max-w-2xl"
          data-testid="hero-subheadline"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("iletisim")}
            className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold bg-[#5B7FFF] text-[#15171C] hover:shadow-[0_0_35px_rgba(91,127,255,0.5)] transition-shadow duration-300"
            data-testid="hero-cta-projeyi-baslat"
          >
            {t.hero.cta1}
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </button>
          <button
            onClick={() => scrollToId("hizmetler")}
            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white border border-white/15 hover:border-[#5B7FFF]/60 hover:text-[#5B7FFF] transition-colors duration-300 backdrop-blur-sm"
            data-testid="hero-cta-hizmetleri-kesfet"
          >
            {t.hero.cta2}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="mt-20 grid grid-cols-3 gap-6 max-w-xl"
          data-testid="hero-stats"
        >
          {t.hero.stats.map(([num, label]) => (
            <div key={label} className="border-s border-white/10 ps-4">
              <div className="font-heading font-black text-2xl md:text-3xl text-white tracking-tighter">{num}</div>
              <div className="text-xs md:text-sm text-slate-500 mt-1">{label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("hizmetler")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ opacity: { delay: 2.2 }, y: { repeat: Infinity, duration: 2 } }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-[#5B7FFF] transition-colors z-10"
        aria-label={t.hero.scroll_aria}
        data-testid="hero-scroll-indicator"
      >
        <ChevronDown size={26} />
      </motion.button>
    </section>
  );
}
